import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, Receipt, Search } from 'lucide-react';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  useLoyaltyTransactions,
  useSettlementHealth,
  type NsuStatus,
} from '../api/use-loyalty-admin';
import { settleCountdown } from '../model/campaign-scope';

const STATUS_LABEL: Record<NsuStatus, string> = {
  prepared: 'Orçamento',
  placed: 'Cobrança',
  confirmed: 'Aguardando 24h',
  settled: 'Liquidado',
  cancelled: 'Cancelado',
};

const STATUS_TONE: Record<NsuStatus, string> = {
  prepared: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  placed: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  confirmed: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
  settled: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  cancelled: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Monitor do ciclo do NSU e da liquidação.
 *
 * A razão de existir está no card vermelho: depois do `confirm`, o ponto só
 * entra na carteira 24 horas depois, por um job no worker. Se esse job parar,
 * NADA quebra do lado do caixa — a venda passa normal e o cliente
 * simplesmente não recebe. Esta tela é onde isso aparece.
 */
export function LoyaltySettlementPage() {
  const { idPassbook } = useParams<{ idPassbook: string }>();
  const navigate = useNavigate();
  const [nsu, setNsu] = useState('');
  const [ssn, setSsn] = useState('');
  const [status, setStatus] = useState<NsuStatus | ''>('');

  const health = useSettlementHealth(idPassbook);
  const transactions = useLoyaltyTransactions(idPassbook, { nsu, ssn, status });

  const overdue = health.data?.overdue ?? 0;

  return (
    <>
      <PageHeader
        title="Liquidação"
        description="O ciclo do NSU e a janela de 24 horas entre confirmar a venda e creditar o ponto."
        actions={
          <Button variant="secondary" onClick={() => navigate(`/loja/${idPassbook}/campanhas/fidelidade`)}>
            <ArrowLeft className="size-4" /> Voltar
          </Button>
        }
      />

      {health.isLoading ? (
        <Skeleton className="mb-6 h-24 rounded-2xl" />
      ) : (
        <Card
          className={`mb-6 flex items-start gap-3 p-4 ${
            overdue > 0 ? 'border-destructive bg-destructive/5' : ''
          }`}
        >
          {overdue > 0 ? (
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
          ) : (
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
          )}
          <div className="min-w-0">
            <p className="font-medium">
              {overdue > 0
                ? `${overdue} venda(s) confirmada(s) passaram da hora de liquidar`
                : 'Liquidação em dia'}
            </p>
            <p className="text-sm text-muted-foreground">
              {overdue > 0
                ? 'O worker de liquidação parou ou está falhando. Nada quebra no caixa: a venda passa normal e o ponto só não entra. Verifique o processo epossible-dispatch-worker.'
                : 'Nenhum NSU passou de 25 horas em "aguardando". Este número tem de ser zero.'}
            </p>
            {health.data?.withFailures ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {health.data.withFailures} com tentativa de liquidação já falhada — o job tenta de
                novo a cada ciclo.
              </p>
            ) : null}
          </div>
        </Card>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {(['prepared', 'placed', 'confirmed', 'settled', 'cancelled'] as NsuStatus[]).map((key) => (
          <Card key={key} className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {STATUS_LABEL[key]}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">
              {health.isLoading ? '—' : (health.data?.[key] ?? 0)}
            </p>
          </Card>
        ))}
      </div>

      <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-3">
        <div>
          <Label htmlFor="filtro-nsu">NSU</Label>
          <Input
            id="filtro-nsu"
            value={nsu}
            onChange={(e) => setNsu(e.target.value)}
            placeholder="16 dígitos"
            inputMode="numeric"
          />
        </div>
        <div>
          <Label htmlFor="filtro-cpf">CPF</Label>
          <Input
            id="filtro-cpf"
            value={ssn}
            onChange={(e) => setSsn(e.target.value)}
            placeholder="só números"
            inputMode="numeric"
          />
        </div>
        <div>
          <Label htmlFor="filtro-status">Estado</Label>
          <Select
            value={status || '__all'}
            onValueChange={(v) => setStatus(v === '__all' ? '' : (v as NsuStatus))}
          >
            <SelectTrigger id="filtro-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Todos</SelectItem>
              {(Object.keys(STATUS_LABEL) as NsuStatus[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {STATUS_LABEL[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {transactions.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-2xl" />
          ))}
        </div>
      ) : (transactions.data ?? []).length === 0 ? (
        <EmptyState
          icon={Search}
          title="Nenhuma venda encontrada"
          description="Ajuste os filtros ou aguarde o primeiro fechamento do caixa."
        />
      ) : (
        <div className="overflow-x-auto rounded-2xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">NSU</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Loja</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Venda</th>
                <th className="px-4 py-3 text-right">Ganha / Gasta</th>
                <th className="px-4 py-3">Liquida em</th>
              </tr>
            </thead>
            <tbody>
              {(transactions.data ?? []).map((row) => (
                <tr key={row._id} className="border-t">
                  <td className="px-4 py-3 font-mono text-xs tabular-nums">
                    {row.nsu}
                    {row.shadow ? (
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        silencioso
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.ssn}</td>
                  <td className="px-4 py-3">{row.storeCode}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${STATUS_TONE[row.status]}`}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                    {row.settleAttempts > 0 && row.status === 'confirmed' ? (
                      <span className="ml-2 text-xs text-destructive">
                        {row.settleAttempts} tentativa(s)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatBRL(row.priceTotal)}
                    {row.priceDiscount > 0 ? (
                      <span className="block text-xs text-muted-foreground">
                        {formatBRL(row.priceDiscount)} de desconto
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className="text-emerald-600">+{row.pointsCredited}</span>
                    {row.pointsDebited > 0 ? (
                      <span className="ml-2 text-rose-600">-{row.pointsDebited}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {row.status === 'settled' ? (
                      <span className="inline-flex items-center gap-1">
                        <Receipt className="size-3" /> {formatDateTime(row.settledAt)}
                      </span>
                    ) : row.status === 'confirmed' ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" /> {settleCountdown(row.status, row.settleAfter)}
                      </span>
                    ) : row.status === 'cancelled' ? (
                      (row.cancelReason ?? 'cancelado')
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
